import { share } from '../../connection';
import {
  type DocClock,
  type DocClocks,
  type DocRecord,
  DocStorageBase,
  type DocUpdate,
} from '../../storage';
import { isBuildDebugEnabled } from '../../utils/build-config';
import { computeChecksum, verifyChecksumDetailed } from '../../utils/checksum';
import { IDBConnection, type IDBConnectionOptions } from './db';
import { IndexedDBLocker } from './lock';
import { withIdbRetry } from './retry';

interface ChannelMessage {
  type: 'update';
  update: DocRecord;
  origin?: string;
}

export class IndexedDBDocStorage extends DocStorageBase<IDBConnectionOptions> {
  static readonly identifier = 'IndexedDBDocStorage';

  readonly connection = share(new IDBConnection(this.options));
  private lastUpdateTimestampMs = 0;

  get db() {
    return this.connection.inner.db;
  }

  get channel() {
    return this.connection.inner.channel;
  }

  override locker = new IndexedDBLocker(this.connection);

  private nextUpdateTimestamp() {
    const now = Date.now();
    const next = Math.max(now, this.lastUpdateTimestampMs + 1);
    this.lastUpdateTimestampMs = next;
    return new Date(next);
  }

  override async pushDocUpdate(update: DocUpdate, origin?: string) {
    return withIdbRetry(this.connection, () =>
      this._pushDocUpdate(update, origin)
    );
  }

  private async _pushDocUpdate(update: DocUpdate, origin?: string) {
    // Debug logging for corruption analysis
    if (isBuildDebugEnabled()) {
      const preview = Array.from(update.bin.slice(0, 20))
        .map(b => b.toString(16).padStart(2, '0'))
        .join(' ');
      console.debug(
        `[IDB] pushDocUpdate: docId=${update.docId}, size=${update.bin.length}, origin=${origin}, preview=[${preview}]`
      );
    }

    // Compute SHA256 checksum for integrity verification
    const checksum = await computeChecksum(update.bin);

    let timestamp = this.nextUpdateTimestamp();

    let retry = 0;

    while (true) {
      let txDone: Promise<void> | undefined;
      try {
        const trx = this.db.transaction(['updates', 'clocks'], 'readwrite');
        txDone = trx.done;

        await trx.objectStore('updates').add({
          ...update,
          createdAt: timestamp,
          checksum,
        });

        await trx.objectStore('clocks').put({ docId: update.docId, timestamp });

        trx.commit();
        await txDone;
      } catch (e) {
        if (e instanceof Error && e.name === 'ConstraintError') {
          await txDone?.catch(() => {});
          retry++;
          if (retry < 10) {
            timestamp = this.nextUpdateTimestamp();
            continue;
          }
        }
        throw e;
      }
      break;
    }

    this.emit(
      'update',
      {
        docId: update.docId,
        bin: update.bin,
        timestamp,
        editor: update.editor,
      },
      origin
    );

    this.channel.postMessage({
      type: 'update',
      update: {
        docId: update.docId,
        bin: update.bin,
        timestamp,
        editor: update.editor,
      },
      origin,
    } satisfies ChannelMessage);

    return { docId: update.docId, timestamp };
  }

  protected override async getDocSnapshot(docId: string) {
    return withIdbRetry(this.connection, () => this._getDocSnapshot(docId));
  }

  private async _getDocSnapshot(docId: string) {
    const trx = this.db.transaction('snapshots', 'readonly');
    const record = await trx.store.get(docId);
    await trx.done;

    if (!record) {
      return null;
    }

    // Verify checksum if present (backwards compat: old records may not have it)
    if (record.checksum) {
      const result = await verifyChecksumDetailed(record.bin, record.checksum);
      if (!result.valid) {
        console.error(
          `[IDB] CHECKSUM MISMATCH in snapshot: docId=${docId}, ` +
            `expected=${result.expected}, actual=${result.actual}, size=${record.bin.length}`
        );
        // Emit corruption event for central handling
        this.emit('corruption', {
          type: 'snapshot',
          docId,
          expected: result.expected,
          actual: result.actual,
        });
      }
    }

    return {
      docId,
      bin: record.bin,
      timestamp: record.updatedAt,
    };
  }

  override async deleteDoc(docId: string) {
    return withIdbRetry(this.connection, () => this._deleteDoc(docId));
  }

  private async _deleteDoc(docId: string) {
    const trx = this.db.transaction(
      ['snapshots', 'updates', 'clocks'],
      'readwrite'
    );

    const idx = trx.objectStore('updates').index('docId');
    const iter = idx.iterate(IDBKeyRange.only(docId));

    for await (const { value } of iter) {
      await trx.objectStore('updates').delete([value.docId, value.createdAt]);
    }

    await trx.objectStore('snapshots').delete(docId);
    await trx.objectStore('clocks').delete(docId);
    trx.commit();
    await trx.done;
  }

  async replaceLocalDoc(record: DocRecord, origin?: string) {
    return withIdbRetry(this.connection, () =>
      this._replaceLocalDoc(record, origin)
    );
  }

  private async _replaceLocalDoc(record: DocRecord, origin?: string) {
    const checksum = await computeChecksum(record.bin);
    const trx = this.db.transaction(
      ['snapshots', 'updates', 'clocks'],
      'readwrite'
    );

    const snapshots = trx.objectStore('snapshots');
    const existingSnapshot = await snapshots.get(record.docId);
    await snapshots.put({
      docId: record.docId,
      bin: record.bin,
      createdAt: existingSnapshot?.createdAt ?? record.timestamp,
      updatedAt: record.timestamp,
      checksum,
    });

    const updates = trx.objectStore('updates');
    const idx = updates.index('docId');
    const iter = idx.iterate(IDBKeyRange.only(record.docId));
    for await (const { value } of iter) {
      await updates.delete([value.docId, value.createdAt]);
    }

    await trx.objectStore('clocks').put({
      docId: record.docId,
      timestamp: record.timestamp,
    });

    trx.commit();
    await trx.done;

    this.emit('update', record, origin);
    this.channel.postMessage({
      type: 'update',
      update: record,
      origin,
    } satisfies ChannelMessage);

    return { docId: record.docId, timestamp: record.timestamp };
  }

  protected override async deleteDocSnapshot(docId: string): Promise<void> {
    return withIdbRetry(this.connection, async () => {
      const trx = this.db.transaction('snapshots', 'readwrite');
      await trx.store.delete(docId);
      trx.commit();
      await trx.done;
    });
  }

  override async getDocTimestamps(after: Date = new Date(0)) {
    return withIdbRetry(this.connection, () => this._getDocTimestamps(after));
  }

  private async _getDocTimestamps(after: Date) {
    const trx = this.db.transaction('clocks', 'readonly');

    const clocks = await trx.store.getAll();
    await trx.done;

    return clocks.reduce((ret, cur) => {
      if (cur.timestamp > after) {
        ret[cur.docId] = cur.timestamp;
      }
      return ret;
    }, {} as DocClocks);
  }

  override async getDocTimestamp(docId: string): Promise<DocClock | null> {
    return withIdbRetry(this.connection, () => this._getDocTimestamp(docId));
  }

  private async _getDocTimestamp(docId: string): Promise<DocClock | null> {
    const trx = this.db.transaction('clocks', 'readonly');

    const record = (await trx.store.get(docId)) ?? null;
    await trx.done;
    return record;
  }

  protected override async setDocSnapshot(
    snapshot: DocRecord
  ): Promise<boolean> {
    return withIdbRetry(this.connection, () => this._setDocSnapshot(snapshot));
  }

  private async _setDocSnapshot(snapshot: DocRecord): Promise<boolean> {
    // Debug logging for corruption analysis
    if (isBuildDebugEnabled()) {
      const preview = Array.from(snapshot.bin.slice(0, 20))
        .map(b => b.toString(16).padStart(2, '0'))
        .join(' ');
      console.debug(
        `[IDB] setDocSnapshot: docId=${snapshot.docId}, size=${snapshot.bin.length}, preview=[${preview}]`
      );
    }

    // Compute SHA256 checksum for integrity verification
    const checksum = await computeChecksum(snapshot.bin);

    const trx = this.db.transaction('snapshots', 'readwrite');
    const record = await trx.store.get(snapshot.docId);

    if (!record || record.updatedAt < snapshot.timestamp) {
      await trx.store.put({
        docId: snapshot.docId,
        bin: snapshot.bin,
        createdAt: record?.createdAt ?? snapshot.timestamp,
        updatedAt: snapshot.timestamp,
        checksum,
      });
    }

    trx.commit();
    await trx.done;
    return true;
  }

  protected override async getDocUpdates(docId: string): Promise<DocRecord[]> {
    return withIdbRetry(this.connection, () => this._getDocUpdates(docId));
  }

  private async _getDocUpdates(docId: string): Promise<DocRecord[]> {
    const trx = this.db.transaction('updates', 'readonly');
    const updates = await trx.store.index('docId').getAll(docId);
    await trx.done;

    // Verify checksums for all updates that have them
    const results: DocRecord[] = [];
    for (const update of updates) {
      if (update.checksum) {
        const result = await verifyChecksumDetailed(
          update.bin,
          update.checksum
        );
        if (!result.valid) {
          console.error(
            `[IDB] CHECKSUM MISMATCH in update: docId=${docId}, ` +
              `timestamp=${update.createdAt.toISOString()}, ` +
              `expected=${result.expected}, actual=${result.actual}, size=${update.bin.length}`
          );
          // Emit corruption event for central handling
          this.emit('corruption', {
            type: 'update',
            docId,
            timestamp: update.createdAt,
            expected: result.expected,
            actual: result.actual,
          });
        }
      }
      results.push({
        docId,
        bin: update.bin,
        timestamp: update.createdAt,
      });
    }

    return results;
  }

  protected override async markUpdatesMerged(
    docId: string,
    updates: DocRecord[]
  ): Promise<number> {
    return withIdbRetry(this.connection, () =>
      this._markUpdatesMerged(docId, updates)
    );
  }

  private async _markUpdatesMerged(
    docId: string,
    updates: DocRecord[]
  ): Promise<number> {
    const trx = this.db.transaction('updates', 'readwrite');

    await Promise.all(
      updates.map(update => trx.store.delete([docId, update.timestamp]))
    );

    trx.commit();
    await trx.done;
    return updates.length;
  }

  private docUpdateListener = 0;

  override subscribeDocUpdate(
    callback: (update: DocRecord, origin?: string) => void
  ): () => void {
    if (this.docUpdateListener === 0) {
      this.channel.addEventListener('message', this.handleChannelMessage);
    }
    this.docUpdateListener++;

    const dispose = super.subscribeDocUpdate(callback);

    return () => {
      dispose();
      this.docUpdateListener--;
      if (this.docUpdateListener === 0) {
        this.channel.removeEventListener('message', this.handleChannelMessage);
      }
    };
  }

  handleChannelMessage = (event: MessageEvent<ChannelMessage>) => {
    if (event.data.type === 'update') {
      this.emit('update', event.data.update, event.data.origin);
    }
  };
}
